/*************************************************************************
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import { discovery } from "dc-core";
import Tooltip from "@react/react-spectrum/Tooltip";
import InteractiveTooltipStyle from "./InteractiveTooltip.css";
import { overlayEvents } from "overlays/OverlayEvents";
import { VERB_PREVIEW_SELECT, TARGET_BUILTIN_TOOLTIP, VERB_COMMENT_FTE } from "constants/enums";

export const InteractiveTooltip = ({
  parentNode,
  pageIndex,
  activeVerb,
  getLatestHitTestResponse,
  getPagesViewBoundingRect
}) => {
  const [tooltipData, setTooltipData] = useState(null);
  const activePageIndex = useRef(-1);
  const parentNodeRect = parentNode ? parentNode.getBoundingClientRect() : { top: 0, left: 0 };
  const timeoutRef = useRef(null);
  const tooltipRef = useRef(null);
  const clientX = tooltipData ? tooltipData.clientX : null;
  const clientY = tooltipData ? tooltipData.clientY : null;

  const resetTooltipData = useCallback(() => {
    setTooltipData(null);
  }, []);

  const removeTooltipAfterTimeout = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      resetTooltipData();
      timeoutRef.current = null;
    }, 15 * 1000);
  }, []);

  /**
   * Clear timeout on unmount
   */
  useEffect(() => {
    if (activeVerb !== VERB_PREVIEW_SELECT) {
      resetTooltipData();
      clearTimeout(timeoutRef.current);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [activeVerb]);

  useEffect(() => {
    const onPageClick = async event => {
      const { contentType } = getLatestHitTestResponse();
      const {
        eventObj: { clientX, clientY }
      } = event;
      activePageIndex.current = event.params.pageIndex;
      if (pageIndex !== activePageIndex.current) {
        resetTooltipData();
        return null;
      }

      try {
        const tooltipText = await discovery.callBootstrapMethod(VERB_COMMENT_FTE, "executeAction", {
          cohortId: TARGET_BUILTIN_TOOLTIP,
          contentType
        });

        if (tooltipText) {
          setTooltipData({ clientX, clientY, tooltipText });
        } else {
          resetTooltipData();
        }

        /**
         * Remove Tooltip after 10 seconds
         */
        removeTooltipAfterTimeout();
      } catch (e) {
        // do nothing
      }
    };
    const onZoom = () => resetTooltipData();
    const unRegisterEvents = () => {
      overlayEvents.unregister("pageclick", onPageClick);
      overlayEvents.unregister("zoom", onZoom);
    };

    overlayEvents.on("pageclick", onPageClick);
    overlayEvents.on("zoom", onZoom);

    /**
     * Unregister old events on re-render.
     */
    return () => {
      unRegisterEvents();
    };
  }, [pageIndex, getLatestHitTestResponse]);

  useLayoutEffect(() => {
    if (clientX !== null && clientY !== null && tooltipRef.current) {
      const { width, height } = tooltipRef.current.getBoundingClientRect();
      const { left, right, top } = getPagesViewBoundingRect();
      let transformX = "-50%";
      let transformY = "-100%";

      if (clientY - top < height) {
        transformY = `0px`;
      }

      if (right - clientX < width / 2) {
        transformX = "-100%";
      }

      if (clientX - left < width / 2) {
        transformX = "0px";
      }

      tooltipRef.current.style.transform = `translate(${transformX}, ${transformY})`;
    }
  }, [clientX, clientY, getPagesViewBoundingRect]);

  if (tooltipData && pageIndex === activePageIndex.current && activeVerb === VERB_PREVIEW_SELECT) {
    const style = {
      top: clientY - parentNodeRect.top,
      left: clientX - parentNodeRect.left,
      position: "absolute"
    };
    return (
      <div className={InteractiveTooltipStyle.tooltipRect} style={style} ref={tooltipRef}>
        <Tooltip placement="top" role="tooltip" variant="info">
          {tooltipData.tooltipText}
        </Tooltip>
      </div>
    );
  }

  return null;
};
